export interface SessionInfo {
  id: string;
  title: string;
  // User-settable name. The server seeds it (working-dir basename / "terminal N")
  // and PATCH /api/sessions/{id} updates it; `title` mirrors the effective name.
  name?: string;
  state: string;
  createdAt: string;
  // tmux session name (e.g. "zw-abc123") when the backend is tmux-backed,
  // allowing the user to `tmux attach -t <name>` the same live terminal from
  // their Mac. Absent when tmux isn't installed on the backend.
  tmux?: string;
}

export interface BackendStatus {
  id: string;
  name: string;
  online: boolean;
}

const BACKEND_KEY = 'zw_backend';

// selectedBackend returns the user's chosen backend id. If none is stored, it
// falls back to the first backend in the supplied list (the relay's primary),
// or '' when no list is given (relay then routes to primary by default).
export function selectedBackend(backends?: { id: string }[]): string {
  const stored = localStorage.getItem(BACKEND_KEY);
  if (stored) return stored;
  return backends && backends.length > 0 ? backends[0].id : '';
}

// setBackend persists the chosen backend id so REST + WS requests target it.
export function setBackend(id: string): void {
  localStorage.setItem(BACKEND_KEY, id);
}

// AuthError signals that the relay rejected the request (no/expired session).
// main.ts catches this and renders the login screen.
export class AuthError extends Error {
  constructor() {
    super('authentication required');
    this.name = 'AuthError';
  }
}

// BackendOfflineError signals that the relay reached us but could not reach the
// laptop backend (502/503/504) — e.g. the WireGuard tunnel is down or zw-server
// isn't running. main.ts catches this and renders a friendly "can't reach your
// laptop" screen instead of a blank page.
export class BackendOfflineError extends Error {
  readonly status: number;
  constructor(status: number) {
    super(`backend offline: ${status}`);
    this.name = 'BackendOfflineError';
    this.status = status;
  }
}

// apiFetch wraps fetch and translates non-OK responses into typed errors:
// 401 → AuthError (login gate), 502/503/504 → BackendOfflineError (backend
// unreachable), and any other non-OK status → a generic Error.
export async function apiFetch(input: string, init?: RequestInit): Promise<Response> {
  const id = selectedBackend();
  const headers = new Headers(init?.headers);
  if (id) headers.set('X-ZW-Backend', id);
  const r = await fetch(input, { ...init, headers });
  if (r.status === 401) throw new AuthError();
  if (r.status === 502 || r.status === 503 || r.status === 504) throw new BackendOfflineError(r.status);
  if (!r.ok) throw new Error(`request failed: ${r.status}`);
  return r;
}

export async function listBackends(): Promise<BackendStatus[]> {
  const r = await apiFetch('/api/backends');
  return r.json();
}

export async function login(password: string): Promise<boolean> {
  const r = await fetch('/login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  return r.ok;
}

export async function logout(): Promise<void> {
  await fetch('/logout', { method: 'POST' });
}

export async function createSession(cols: number, rows: number): Promise<string> {
  const r = await apiFetch('/api/sessions', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ cols, rows }),
  });
  return (await r.json()).id;
}

export async function listSessions(): Promise<SessionInfo[]> {
  const r = await apiFetch('/api/sessions');
  return r.json();
}

export async function deleteSession(id: string): Promise<void> {
  await apiFetch(`/api/sessions/${id}`, { method: 'DELETE' });
}

// renameSession sets a terminal's display name. PATCH returns {id,name}; we only
// need the call to succeed (the caller updates its row optimistically).
export async function renameSession(id: string, name: string): Promise<void> {
  await apiFetch(`/api/sessions/${id}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ name }),
  });
}
